import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import {
  buildEqCurveCommands,
  buildEqLoginStatusCommand,
  buildEqModeCommand,
  buildEqSinglePointCommand,
  type EqDataPoint,
} from '@/api/device/commands'
import type { EqMode, EqPresetData } from '@/api/eq'
import {
  addEqPreset,
  getEqList,
  pinEqPreset,
  removeEqPreset,
  renameEqPreset,
  resetEqPreset,
  setLastEqPreset,
  updateEqPoint,
} from '@/api/eq'
import { useUserStore } from './user'

export const EQ_DISPLAY_FREQUENCIES = [
  '20Hz', '60Hz', '120Hz', '250Hz', '500Hz', '800Hz', '1K',
  '2K', '4K', '6K', '8K', '10K', '12K', '20K',
] as const

const PROTOCOL_FREQUENCIES = [
  '25HZ', '50HZ', '75HZ', '150HZ', '300HZ', '600HZ', '1K',
  '2K', '3K', '6K', '8K', '10K', '12K', '16K',
]
const DEFAULT_Q = '0.75'
const MAX_CUSTOM_PRESETS = 20
const MODE_TO_HARDWARE: Record<EqMode, number> = { game: 0, movie: 1, music: 2 }
const HARDWARE_TO_MODE: EqMode[] = ['game', 'movie', 'music']

function makePreset(id: string, name: string, gains: number[], qValues: number[]): EqPresetData {
  return {
    id,
    name,
    datapoint: PROTOCOL_FREQUENCIES.map((freq, index) => ({
      fid: index + 1,
      freq,
      gain: gains[index] ?? 0,
      q: String(qValues[index] ?? DEFAULT_Q),
    })),
  }
}

// 内置值与 oldcode/js/eq_setting.js 完全一致。
const SYSTEM_PRESETS: Record<EqMode, EqPresetData[]> = {
  game: [
    makePreset('TacticalFPS', '战术竞技射击', [-5, -3, -1, 0, -4, 2, 6, 5, 4, 3, 2, 1, 0, 0], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('BattleRoyale', '大逃杀', [3.5, 4, 2, 3, -3.5, -1.5, 3, 4, 2, 4, 3, 2, 0, 0], [1.2, 1.4, 1.6, .7, 1.5, 1.2, 1.5, 1.8, 2, 2.2, 2, 1.5, 1, .75]),
    makePreset('MilitaryShooter', '军事射击', [3, 3, 1, 5, -4, 0, 2, 4, 3, 5, 4, 2, 0, -1], [1, 1.2, 1.4, .6, 1.8, 1.5, 1.8, 2, 2.5, 2, 1.5, 1.2, 1, .75]),
    makePreset('ImmersiveRPG', '沉浸式角色扮演', [3, 4, 3, 4, -1.5, 0, 2, 1, -1, 1, 3, 3, 2, 0], [1, 1.2, 1.4, .7, 1.5, 1.2, 1.5, 1.8, 2, 2, 1.5, 1.2, 1, .75]),
  ],
  music: [
    makePreset('Pop Mode', '流行歌曲', [4, 2, 0, 3, -4, -1, 4, 3, -1, 2, 1, 4, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.2, 1.5, 2, 2.5, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Bass+', '低音加强', [3.5, 6, 4, 5, -5, -3, 0, 1, -2, 0, 0, 3, 0, 0], [1.2, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 1.5, 1.2, 1, 1, .75]),
    makePreset('Vocal Focus', '清爽人声', [-3, -2, -1, 1, -4, 0, 5, 6, 4, 2, 1, 3, 1, 0], [1, 1.2, 1.4, .5, 1.8, 1.5, 1.8, 2.2, 2.5, 1.8, 1.5, 1.5, 1, .75]),
    makePreset('DJ Mode', 'DJ专用', [3, 6, 3, 2, -5, -1, 1, 2, -1, 4, 3, 5, 2, 0], [1, 1.4, 1.6, .6, 1.8, 1.5, 1.2, 1.5, 2, 2, 1.8, 1.2, 1, .75]),
  ],
  movie: [
    makePreset('Theater Surround', '影视音效', [4, 5, 4, 3, -4, -1, 4, 3, 1, 2, 3, 4, 2, 0], [1.2, 1.5, 1.6, .6, 1.8, 1.2, 1.5, 1.8, 2, 1.8, 1.5, 1.2, 1, .75]),
    makePreset('Horror Atmosphere', '恐怖氛围', [3, 3, 0, -2, -4, -1, 1, 3, 2, 5, 6, 4, 3, 1], [1, 1.4, 1.5, .8, 1.8, 1.5, 1.5, 2.2, 2.5, 2, 2.5, 1.5, 1.2, .75]),
    makePreset('Sci-Fi Space', '科幻空间', [4, 5, 3, 2, -4, -3, 1, 2, 3, 4, 5, 5, 3, 1], [1.2, 1.5, 1.6, .6, 1.8, 1.5, 1.5, 1.8, 2, 2, 2.2, 1.5, 1.2, .75]),
  ],
}

const SYSTEM_PRESET_IDS: Record<EqMode, string[]> = {
  game: SYSTEM_PRESETS.game.map(item => item.id),
  music: SYSTEM_PRESETS.music.map(item => item.id),
  movie: SYSTEM_PRESETS.movie.map(item => item.id),
}

function clonePoints(points: EqDataPoint[]): EqDataPoint[] {
  return points.map(point => ({ ...point, gain: Number(point.gain) || 0, q: String(point.q || DEFAULT_Q) }))
}

function clonePresets(presets: EqPresetData[]): EqPresetData[] {
  return presets.map(preset => ({ ...preset, datapoint: clonePoints(preset.datapoint) }))
}

function createZeroCurve(template?: EqDataPoint[]): EqDataPoint[] {
  return PROTOCOL_FREQUENCIES.map((freq, index) => ({
    fid: index + 1,
    freq: template?.[index]?.freq || freq,
    gain: 0,
    q: template?.[index]?.q || DEFAULT_Q,
  }))
}

interface EqModeState {
  enabled: boolean
  currentPreset: string
  systemPresets: EqPresetData[]
  customPresets: EqPresetData[]
  lastPreset: string | null
  pinnedPreset: string | null
}

function createModeState(mode: EqMode): EqModeState {
  return {
    enabled: false,
    currentPreset: SYSTEM_PRESETS[mode][0].id,
    systemPresets: clonePresets(SYSTEM_PRESETS[mode]),
    customPresets: [],
    lastPreset: null,
    pinnedPreset: null,
  }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export const useEqStore = defineStore('eq', () => {
  const userStore = useUserStore()
  userStore.loadFromStorage()
  const gameEq = ref<EqModeState>(createModeState('game'))
  const musicEq = ref<EqModeState>(createModeState('music'))
  const movieEq = ref<EqModeState>(createModeState('movie'))
  const activeMode = ref<EqMode>('game')
  const appliedMode = ref<EqMode | null>(null)
  const appliedPresetId = ref<string | null>(null)
  const remoteLoaded = ref<Record<EqMode, boolean>>({ game: false, music: false, movie: false })
  const apiDebounceTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})
  const lastPresetTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({})
  let deviceQueue: Promise<void> = Promise.resolve()

  function getModeState(mode: EqMode): EqModeState {
    if (mode === 'music') return musicEq.value
    if (mode === 'movie') return movieEq.value
    return gameEq.value
  }

  function presetsForMode(mode: EqMode): EqPresetData[] {
    const state = getModeState(mode)
    return [...state.systemPresets, ...state.customPresets]
  }

  const currentModeState = computed(() => getModeState(activeMode.value))
  const currentSettings = currentModeState
  const allPresets = computed(() => presetsForMode(activeMode.value))
  const currentPreset = computed(() => allPresets.value.find(preset => preset.id === currentModeState.value.currentPreset))
  const currentPresetData = computed(() => currentPreset.value?.datapoint || createZeroCurve())
  const isSystemPreset = computed(() => (presetId: string) => SYSTEM_PRESET_IDS[activeMode.value].includes(presetId))
  const canAddMoreCustom = computed(() => currentModeState.value.customPresets.length < MAX_CUSTOM_PRESETS)
  const isEnabled = computed(() => appliedMode.value !== null && !!getModeState(appliedMode.value).enabled)
  const currentMode = computed(() => activeMode.value)
  const slidersInteractive = computed(() => (
    isEnabled.value
    && appliedMode.value === activeMode.value
    && appliedPresetId.value === currentModeState.value.currentPreset
  ))

  function isLoggedIn(): boolean {
    return userStore.isLoggedIn && !!userStore.uuid
  }

  function getUuid(): string {
    return userStore.uuid || ''
  }

  function getLinktype(): string {
    return hidManager.getConnectionType() === '2.4G' ? '0' : '1'
  }

  function normalizePreset(preset: EqPresetData): EqPresetData {
    return { ...preset, datapoint: clonePoints(preset.datapoint) }
  }

  function enqueueDevice(task: () => Promise<void>): Promise<void> {
    deviceQueue = deviceQueue.then(task).catch(error => {
      console.error('[EQ] 设备命令执行失败:', error)
    })
    return deviceQueue
  }

  async function sendLoginStatus(): Promise<void> {
    await hidManager.sendData(buildEqLoginStatusCommand(isLoggedIn()), false)
  }

  async function getPositioningProtocolState(): Promise<{ enabled: boolean; tag: number }> {
    try {
      const { useChijiStore } = await import('./chiji')
      const store = useChijiStore()
      const index = store.allPresets.findIndex(item => item.id === store.currentPresetId)
      return { enabled: store.enabled, tag: Math.max(0, index) }
    } catch {
      return { enabled: false, tag: 0 }
    }
  }

  async function sendModeState(enabled: boolean, mode: EqMode, presetId: string | null): Promise<void> {
    if (!hidManager.isConnected()) return
    const positioning = await getPositioningProtocolState()
    const tag = enabled && presetId
      ? Math.max(0, presetsForMode(mode).findIndex(preset => preset.id === presetId))
      : 0
    await sendLoginStatus()
    await hidManager.sendData(buildEqModeCommand(
      enabled,
      MODE_TO_HARDWARE[mode],
      tag,
      positioning.enabled,
      positioning.tag,
    ), false)
  }

  async function sendCurve(points: EqDataPoint[], includeLogin = true): Promise<void> {
    if (!hidManager.isConnected()) return
    if (includeLogin) await sendLoginStatus()
    const commands = buildEqCurveCommands(points)
    for (let index = 0; index < commands.length; index += 1) {
      await hidManager.sendData(commands[index], false)
      if (index < commands.length - 1) await delay(200)
    }
  }

  function setActiveMode(mode: EqMode) {
    activeMode.value = mode
  }

  const switchMode = setActiveMode

  async function applyPreset(mode: EqMode, presetId: string): Promise<void> {
    const state = getModeState(mode)
    const preset = presetsForMode(mode).find(item => item.id === presetId)
    if (!preset) return

    try {
      const { useChijiStore } = await import('./chiji')
      const chijiStore = useChijiStore()
      if (chijiStore.enabled) await chijiStore.setEnabled(false)
    } catch {
      // 状态仓库可以在测试中独立使用。
    }

    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    activeMode.value = mode
    state.enabled = true
    state.currentPreset = presetId
    state.lastPreset = presetId
    appliedMode.value = mode
    appliedPresetId.value = presetId
    saveToStorage()
    scheduleSetLastPreset(mode, presetId)

    await enqueueDevice(async () => {
      await sendModeState(true, mode, presetId)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(preset.datapoint, false)
    })
  }

  async function setEqEnabled(enabled: boolean): Promise<void> {
    if (enabled) {
      await applyPreset(activeMode.value, currentModeState.value.currentPreset)
      return
    }

    const mode = appliedMode.value || activeMode.value
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    appliedMode.value = null
    appliedPresetId.value = null
    saveToStorage()

    await enqueueDevice(async () => {
      await sendModeState(false, mode, null)
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(createZeroCurve(), false)
    })
  }

  async function selectPreset(presetId: string): Promise<void> {
    await applyPreset(activeMode.value, presetId)
  }

  async function updateEqGain(bandIndex: number, gain: number, skipDeviceSend = false): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.gain = Math.max(-12, Math.min(12, Math.round(gain * 2) / 2))

    if (!skipDeviceSend && hidManager.isConnected()) {
      const command = buildEqSinglePointCommand(bandIndex, point)
      void enqueueDevice(async () => {
        await sendLoginStatus()
        await hidManager.sendData(command, false)
      })
    }

    if (isLoggedIn()) {
      debouncedSyncPointToApi(activeMode.value, preset.id, bandIndex, point)
    }
  }

  async function updateEqFrequency(bandIndex: number, frequency: string): Promise<void> {
    const preset = currentPreset.value
    const point = preset?.datapoint[bandIndex]
    if (!preset || !point || !slidersInteractive.value) return
    point.freq = frequency
    await updateEqGain(bandIndex, point.gain)
  }

  function debouncedSyncPointToApi(mode: EqMode, presetId: string, bandIndex: number, point: EqDataPoint) {
    const key = `${mode}-${presetId}-${bandIndex}`
    if (apiDebounceTimers.value[key]) clearTimeout(apiDebounceTimers.value[key])
    apiDebounceTimers.value[key] = setTimeout(async () => {
      const uuid = getUuid()
      if (!uuid) return
      try {
        await updateEqPoint(mode, uuid, presetId, point, getLinktype())
      } catch (error) {
        console.error('[EQ] 同步频点失败:', error)
      }
      delete apiDebounceTimers.value[key]
    }, 500)
  }

  function scheduleSetLastPreset(mode: EqMode, presetId: string): void {
    if (!isLoggedIn()) return
    const key = `${mode}-${presetId}`
    if (lastPresetTimers.value[key]) clearTimeout(lastPresetTimers.value[key])
    lastPresetTimers.value[key] = setTimeout(async () => {
      try {
        await setLastEqPreset(mode, getUuid(), presetId, getLinktype())
      } catch (error) {
        console.error('[EQ] 保存上次预设失败:', error)
      }
      delete lastPresetTimers.value[key]
    }, 200)
  }

  async function applyCurrentEq(): Promise<void> {
    const preset = currentPreset.value
    if (!preset) return
    await enqueueDevice(() => sendCurve(preset.datapoint))
  }

  async function addCustomPreset(name: string, datapoint: EqDataPoint[]): Promise<string | null> {
    const state = currentModeState.value
    if (state.customPresets.length >= MAX_CUSTOM_PRESETS) throw new Error('CUSTOM_PRESET_LIMIT')
    const newPreset: EqPresetData = {
      id: `custom-${activeMode.value}-${Date.now()}`,
      name: name.trim(),
      datapoint: clonePoints(datapoint),
      isCustom: true,
      createdAt: Date.now(),
    }

    if (isLoggedIn()) {
      try {
        const response = await addEqPreset(activeMode.value, getUuid(), newPreset, getLinktype())
        if (response.code === 1 && response.data?.id) newPreset.id = response.data.id
      } catch (error) {
        console.error('[EQ] 添加预设失败:', error)
      }
    }

    state.customPresets.push(newPreset)
    await applyPreset(activeMode.value, newPreset.id)
    return newPreset.id
  }

  async function deleteCustomPreset(presetId: string): Promise<boolean> {
    const state = currentModeState.value
    const index = state.customPresets.findIndex(preset => preset.id === presetId)
    if (index < 0) return false
    if (isLoggedIn()) {
      try {
        const response = await removeEqPreset(activeMode.value, getUuid(), presetId, getLinktype())
        if (response.code !== 1) return false
      } catch (error) {
        console.error('[EQ] 删除预设失败:', error)
        return false
      }
    }
    state.customPresets.splice(index, 1)
    const fallback = state.systemPresets[0]?.id || ''
    state.currentPreset = fallback
    if (appliedMode.value === activeMode.value && appliedPresetId.value === presetId && fallback) {
      await applyPreset(activeMode.value, fallback)
    }
    saveToStorage()
    return true
  }

  async function renameCustomPreset(presetId: string, name: string): Promise<boolean> {
    const preset = currentModeState.value.customPresets.find(item => item.id === presetId)
    const normalizedName = name.trim().slice(0, 20)
    if (!preset || !normalizedName) return false
    if (presetsForMode(activeMode.value).some(item => item.id !== presetId && item.name.toLowerCase() === normalizedName.toLowerCase())) {
      return false
    }
    if (isLoggedIn()) {
      try {
        const response = await renameEqPreset(activeMode.value, getUuid(), presetId, normalizedName, getLinktype())
        if (response.code !== 1) return false
      } catch (error) {
        console.error('[EQ] 重命名预设失败:', error)
        return false
      }
    }
    preset.name = normalizedName
    saveToStorage()
    return true
  }

  async function resetCurrentPreset(): Promise<boolean> {
    const preset = currentPreset.value
    if (!preset || preset.isCustom) return false
    const source = SYSTEM_PRESETS[activeMode.value].find(item => item.id === preset.id)
    if (!source) return false
    if (isLoggedIn()) {
      try {
        const response = await resetEqPreset(activeMode.value, getUuid(), preset.id, getLinktype())
        if (response.code !== 1) return false
      } catch (error) {
        console.error('[EQ] 重置预设失败:', error)
        return false
      }
    }
    preset.datapoint = clonePoints(source.datapoint)
    if (slidersInteractive.value) await applyCurrentEq()
    return true
  }

  async function pinPreset(presetId: string): Promise<boolean> {
    const state = currentModeState.value
    if (!presetsForMode(activeMode.value).some(preset => preset.id === presetId)) return false
    if (isLoggedIn()) {
      try {
        const response = await pinEqPreset(activeMode.value, getUuid(), presetId, getLinktype())
        if (response.code !== 1) return false
      } catch (error) {
        console.error('[EQ] 置顶预设失败:', error)
        return false
      }
    }
    state.pinnedPreset = presetId
    saveToStorage()
    return true
  }

  async function loadFromRemote(mode: EqMode): Promise<boolean> {
    if (!isLoggedIn() || remoteLoaded.value[mode]) return false
    const state = getModeState(mode)
    try {
      const response = await getEqList(mode, getUuid(), getLinktype())
      if (response.code !== 1 || !response.data) return false
      const presets = response.data.presets.map(normalizePreset)
      const remoteSystem = presets.filter(preset => SYSTEM_PRESET_IDS[mode].includes(preset.id))
      const remoteCustom = presets.filter(preset => !SYSTEM_PRESET_IDS[mode].includes(preset.id))
      if (remoteSystem.length) state.systemPresets = remoteSystem
      state.customPresets = remoteCustom.map((preset, index) => ({
        ...preset,
        isCustom: true,
        createdAt: preset.createdAt || Date.now() + index,
      }))
      state.lastPreset = response.data.lastPreset || null
      state.pinnedPreset = response.data.defaultPreset || null
      if (state.lastPreset && presetsForMode(mode).some(preset => preset.id === state.lastPreset)) {
        state.currentPreset = state.lastPreset
      }
      remoteLoaded.value[mode] = true
      return true
    } catch (error) {
      console.error(`[EQ] 加载 ${mode} 模式预设失败:`, error)
      return false
    }
  }

  async function loadAllFromRemote(): Promise<void> {
    if (!isLoggedIn()) return
    await Promise.all([loadFromRemote('game'), loadFromRemote('music'), loadFromRemote('movie')])
  }

  function syncHardwareStatus(enabled: boolean, modeValue: number, presetIndex: number): void {
    if (isLoggedIn()) return
    const mode = HARDWARE_TO_MODE[Math.max(0, Math.min(2, modeValue))] || 'game'
    const state = getModeState(mode)
    const preset = presetsForMode(mode)[Math.max(0, presetIndex)] || state.systemPresets[0]
    ;[gameEq.value, musicEq.value, movieEq.value].forEach(item => { item.enabled = false })
    activeMode.value = mode
    state.currentPreset = preset.id
    state.enabled = enabled
    appliedMode.value = enabled ? mode : null
    appliedPresetId.value = enabled ? preset.id : null
  }

  function loadFromStorage(): void {
    if (!isLoggedIn()) return
    try {
      const raw = localStorage.getItem('eqSettings')
      if (!raw) return
      const saved = JSON.parse(raw)
      if (['game', 'music', 'movie'].includes(saved.activeMode)) activeMode.value = saved.activeMode
      for (const mode of ['game', 'music', 'movie'] as EqMode[]) {
        const target = getModeState(mode)
        const source = saved.modes?.[mode]
        if (!source) continue
        if (Array.isArray(source.customPresets)) target.customPresets = source.customPresets.map(normalizePreset)
        if (source.currentPreset && presetsForMode(mode).some(preset => preset.id === source.currentPreset)) {
          target.currentPreset = source.currentPreset
        }
        target.pinnedPreset = source.pinnedPreset || null
        target.lastPreset = source.lastPreset || null
      }
    } catch (error) {
      console.warn('[EQ] 加载本地状态失败:', error)
    }
  }

  function saveToStorage(): void {
    if (!isLoggedIn()) return
    try {
      const modes = Object.fromEntries((['game', 'music', 'movie'] as EqMode[]).map(mode => {
        const state = getModeState(mode)
        return [mode, {
          currentPreset: state.currentPreset,
          customPresets: state.customPresets,
          lastPreset: state.lastPreset,
          pinnedPreset: state.pinnedPreset,
        }]
      }))
      localStorage.setItem('eqSettings', JSON.stringify({ activeMode: activeMode.value, modes }))
    } catch (error) {
      console.warn('[EQ] 保存本地状态失败:', error)
    }
  }

  watch(() => userStore.isLoggedIn, loggedIn => {
    if (loggedIn) {
      loadFromStorage()
      void loadAllFromRemote()
      return
    }
    gameEq.value = createModeState('game')
    musicEq.value = createModeState('music')
    movieEq.value = createModeState('movie')
    activeMode.value = 'game'
    appliedMode.value = null
    appliedPresetId.value = null
    remoteLoaded.value = { game: false, music: false, movie: false }
    localStorage.removeItem('eqSettings')
  })

  return {
    gameEq,
    musicEq,
    movieEq,
    activeMode,
    appliedMode,
    appliedPresetId,
    remoteLoaded,
    currentModeState,
    currentSettings,
    allPresets,
    currentPreset,
    currentPresetData,
    isSystemPreset,
    canAddMoreCustom,
    isEnabled,
    currentMode,
    slidersInteractive,
    setActiveMode,
    switchMode,
    setEqEnabled,
    selectPreset,
    updateEqGain,
    updateEqFrequency,
    applyCurrentEq,
    addCustomPreset,
    deleteCustomPreset,
    renameCustomPreset,
    resetCurrentPreset,
    pinPreset,
    loadFromRemote,
    loadAllFromRemote,
    syncHardwareStatus,
    loadFromStorage,
    saveToStorage,
  }
})
